import { Form, Input, Button, message, Result } from 'antd';
import { MailOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '@tradermind/shared/api';
import { forgotPasswordSchema, type ForgotPasswordFormData } from '@tradermind/shared/validators';
import { ROUTES } from '@tradermind/shared/constants';

export default function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const [submitted, setSubmitted] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: authApi.forgotPassword,
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (error: { error?: { message?: string } }) => {
      message.error(error.error?.message || t('forgot_password.failed'));
    },
  });

  const onSubmit = (data: ForgotPasswordFormData) => {
    forgotPasswordMutation.mutate(data);
  };

  if (submitted) {
    return (
      <Result
        status="success"
        title={t('forgot_password.success_title')}
        subTitle={t('forgot_password.success_desc')}
        extra={
          <Link to={ROUTES.AUTH.LOGIN}>
            <Button type="primary">{t('forgot_password.back_to_login')}</Button>
          </Link>
        }
      />
    );
  }

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <Link to={ROUTES.AUTH.LOGIN} style={{ color: '#6B7280' }}>
          <ArrowLeftOutlined /> {t('forgot_password.back_to_login')}
        </Link>
      </div>

      <h2 style={{ marginBottom: 8 }}>{t('forgot_password.title')}</h2>
      <p style={{ color: '#6B7280', marginBottom: 24 }}>
        {t('forgot_password.description')}
      </p>

      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <Form.Item
          label={t('forgot_password.email')}
          validateStatus={errors.email ? 'error' : ''}
          help={errors.email?.message}
        >
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                prefix={<MailOutlined />}
                placeholder={t('forgot_password.email_placeholder')}
                size="large"
              />
            )}
          />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={forgotPasswordMutation.isPending}
        >
          {t('forgot_password.submit')}
        </Button>
      </Form>
    </>
  );
}
